import { yupResolver } from '@hookform/resolvers/yup';
import { Role } from '@zorro/clients';
import { SUCCESS_MESSAGES, VALIDATION_MESSAGES } from '@zorro/common-utils';
import { Alert } from '@zorro/design-system/v2/Alert';
import { useDrawerFooter } from '@zorro/design-system/v2/Drawer';
import { List } from '@zorro/design-system/v2/List';
import {
  formatDateISO,
  getNowAsDate,
  getTodayISO,
  parseDate,
  parseDateISO,
} from '@zorro/shared/formatters';
import {
  DateInput,
  FormErrorMessage,
  Grid,
  LabelWithTooltip,
  MonthPickerInput,
  Space,
  Stack,
  Text,
} from '@zorro/web/design-system';
import {
  useBatchCallEndpoint,
  useForm,
  useIdempotencyKey,
  useMonolithMutation,
  useRoles,
} from '@zorro/web/ui-utils';
import { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import * as yup from 'yup';

import { FormFooter } from '../../FormFooter';
import { RecordForActions } from '../EmployeeActionsUtils';
import { ErrorsTable } from '../Errors/ErrorsTable';

const terminateEmployeeFormSchema = yup.object({
  terminationDate: yup
    .date()
    .typeError(VALIDATION_MESSAGES.lastDateOfEmploymentRequired)
    .required(VALIDATION_MESSAGES.lastDateOfEmploymentRequired),
  eligibleUntil: yup
    .date()
    .typeError(VALIDATION_MESSAGES.lastDateOfBenefitEligibilityRequired)
    .required(VALIDATION_MESSAGES.lastDateOfBenefitEligibilityRequired),
});

const prepareTerminationPayload = (formFields: TerminateEmployeeFormFields) => {
  const { terminationDate, eligibleUntil } = formFields;

  if (!(terminationDate && eligibleUntil)) {
    return null;
  }

  return {
    terminationDate: formatDateISO(terminationDate),
    eligibleUntil: formatDateISO(eligibleUntil),
    terminationReportDate: getTodayISO(),
  };
};

type TerminateEmployeeFormFields = yup.InferType<
  typeof terminateEmployeeFormSchema
>;

type TerminateEmployeeFormProps = {
  recordsForActions: RecordForActions[];
  onSuccess?: () => Promise<void> | void;
  onClose?: () => void;
};

export function TerminateEmployeeForm({
  recordsForActions,
  onSuccess,
  onClose,
}: TerminateEmployeeFormProps) {
  const { isZorroOperations, userRoles } = useRoles();
  const isBulk = recordsForActions.length > 1;
  const singleEmployee = recordsForActions[0];
  const idempotencyKey = useIdempotencyKey();

  const { tryMutate: mutateTerminateEmployee } = useMonolithMutation({
    method: 'employeesControllerTerminateEmployee',
    successMessage: SUCCESS_MESSAGES.EMPLOYEE_TERMINATED_SUCCESSFULLY_MESSAGE,
  });

  const {
    handleSubmit,
    control,
    formState: { isValid, isSubmitting, errors },
  } = useForm<TerminateEmployeeFormFields>({
    mode: 'all',
    resolver: yupResolver(terminateEmployeeFormSchema),
    defaultValues: {
      terminationDate: singleEmployee?.terminationDate
        ? parseDateISO(singleEmployee.terminationDate).toDate()
        : undefined,
      eligibleUntil: singleEmployee?.eligibleUntil
        ? parseDateISO(singleEmployee.eligibleUntil).toDate()
        : undefined,
    },
  });

  const { executeBatchCall: batchTerminateEmployees, errors: batchErrors } =
    useBatchCallEndpoint({
      methodName: 'employeesControllerTerminateEmployee',
      singularItemName: 'employee',
      action: 'terminated',
      batchSize: 5,
    });

  const terminateEmployee = async (formFields: TerminateEmployeeFormFields) => {
    const payload = prepareTerminationPayload(formFields);
    if (!payload) return;

    const mutateResult = await mutateTerminateEmployee([
      singleEmployee.employeeId,
      idempotencyKey.get(),
      payload,
    ]);

    if (mutateResult.isOk()) {
      await onSuccess?.();
    }
  };

  const terminateEmployeesBulk = async (
    formFields: TerminateEmployeeFormFields
  ) => {
    const payload = prepareTerminationPayload(formFields);
    if (!payload) return;

    const batchItems = recordsForActions.map(({ fullName, employeeId }) => ({
      key: fullName,
      params: [employeeId, idempotencyKey.getFor(employeeId), payload] as const,
    }));

    await batchTerminateEmployees(batchItems, { onSuccess });
  };

  const isEligibleUntilUnlimited =
    isZorroOperations || userRoles.includes(Role.ACCOUNT_SUPERVISOR);

  const handleSubmitForm = isBulk ? terminateEmployeesBulk : terminateEmployee;

  const infoBoxItems = isBulk
    ? [
        'Set termination dates for all selected employees',
        'After you submit the termination, the Zorro team will cancel their Zorro payment cards',
        'Terminated employees will be notified by email with instructions on how to set-up payment independently, should they wish to continue their coverage',
      ]
    : [
        "After you terminate an employee on the Zorro platform, the Zorro team will cancel the employee's Zorro payment card",
        'The terminated employee will be notified with instructions on how to set-up payment independently, should they wish to continue their coverage',
      ];

  const drawerFooter = useMemo(
    () => (
      <FormFooter
        withPaper={false}
        primaryLabel="Submit"
        primaryButtonProps={{
          onClick: handleSubmit(handleSubmitForm),
          disabled: !isValid || isSubmitting,
        }}
        secondaryLabel="Cancel"
        secondaryButtonProps={{
          onClick: onClose,
        }}
      />
    ),
    [handleSubmit, handleSubmitForm, isValid, isSubmitting, onClose]
  );

  useDrawerFooter(drawerFooter);

  if (!isBulk && !singleEmployee) {
    return null;
  }

  const renderDateFields = () => (
    <Grid>
      <Grid.Col span={{ sm: 6 }}>
        <Controller
          control={control}
          name="terminationDate"
          render={({ field }) => (
            <DateInput {...field} required label="Last date of employment" />
          )}
        />
        <FormErrorMessage errors={errors} fieldName="terminationDate" />
      </Grid.Col>
      <Grid.Col span={{ sm: 6 }}>
        <Controller
          control={control}
          name="eligibleUntil"
          render={({ field: { onChange, ...rest } }) => (
            <MonthPickerInput
              {...rest}
              type="default"
              onChange={(date) => {
                if (date instanceof Date) {
                  const lastDayOfMonth = parseDate(date)
                    .endOf('month')
                    .toDate();
                  onChange(lastDayOfMonth);
                }
              }}
              minDate={isEligibleUntilUnlimited ? undefined : getNowAsDate()}
              placeholder="MM/DD/YYYY"
              label={
                <LabelWithTooltip
                  label="Last date of benefit eligibility"
                  tooltipLabel="Backdating is disabled for months with already paid premiums. Contact support if needed"
                  isDisabled={isEligibleUntilUnlimited}
                  isRequired
                />
              }
            />
          )}
        />
        <FormErrorMessage errors={errors} fieldName="eligibleUntil" />
      </Grid.Col>
    </Grid>
  );

  return (
    <Stack>
      {isBulk && (
        <Text size="lg" fw={500}>
          Terminating {recordsForActions.length} employees
        </Text>
      )}
      <Alert title="Here's what to expect:">
        <List size="sm">
          {infoBoxItems.map((item) => (
            <List.Item key={item}>{item}</List.Item>
          ))}
        </List>
      </Alert>
      <Space h="xs" />
      {!isBulk && (
        <Text size="lg" fw={500}>
          {`${singleEmployee.fullName}'s termination details`}
        </Text>
      )}

      <form onSubmit={handleSubmit(handleSubmitForm)}>
        {renderDateFields()}

        {isBulk && (
          <ErrorsTable
            tableTitle="Employee name"
            errors={batchErrors}
            isBulk={isBulk}
          />
        )}
      </form>
    </Stack>
  );
}
